'use client'

import { getSimulatorGameDetail } from '@/apis/simulatorGame'
import useI18n from '@/hooks/useI18n'
import { withLocale } from '@/locales'
import type { SimulatorGameProject } from '@/types/simulatorGame'
import {
  getLocalizedSimulatorGamePath,
  getSimulatorGameId,
} from '@/utils/simulatorGameNavigation'
import dynamic from 'next/dynamic'
import { useRouter } from 'next/navigation'
import { useCallback, useMemo, useState } from 'react'

const WorldGameProfileModal = dynamic(
  () => import('@/components/simulator-game/WorldGameProfileModal'),
  { ssr: false }
)

export type NavigateSimulatorGameOptions = {
  gameId?: string | number | null
  projectId?: string | number | null
  versionChange?: boolean | null
  mode: 'desktop' | 'mobile'
}

export type OpenWorldGameProfileModalOptions = {
  gameId?: string | number | null
  projectId?: string | number | null
  fromGame?: boolean
}

export type NavigateSimulatorGameHandler = (
  options: NavigateSimulatorGameOptions
) => Promise<void>

export default function useNavigateSimulatorGame() {
  const { lang } = useI18n()
  const router = useRouter()
  const [modalState, setModalState] = useState<{
    project: SimulatorGameProject
    fromGame: boolean
  } | null>(null)
  const modalProject = modalState?.project ?? null

  const closeWorldGameProfileModal = useCallback(() => {
    setModalState(null)
  }, [])

  const modalGameId = useMemo(
    () => String(modalProject?.game_id || modalProject?.project_id || ''),
    [modalProject]
  )

  const startModalGame = useCallback(() => {
    if (!modalGameId) return
    setModalState(null)
    router.push(withLocale(`/world/${modalGameId}`, lang))
  }, [lang, modalGameId, router])

  const navigateToSimulatorGame = useCallback(
    async ({
      gameId,
      projectId,
      versionChange,
      mode,
    }: NavigateSimulatorGameOptions) => {
      const id = getSimulatorGameId({ gameId, projectId })
      if (!id) {
        router.push(withLocale('/', lang))
        return
      }

      try {
        const project = await getSimulatorGameDetail(id, lang)
        const latestId = String(project.game_id || project.project_id || id)

        if (mode === 'desktop' && project.version_change) {
          setModalState({ project, fromGame: false })
          return
        }

        router.push(
          getLocalizedSimulatorGamePath({
            gameId: latestId,
            versionChange: project.version_change,
            lang,
          })
        )
      } catch (error) {
        console.error('Failed to resolve simulator game navigation:', error)

        if (mode === 'desktop' && versionChange) {
          router.push(withLocale(`/world/${id}/profile`, lang))
          return
        }

        router.push(
          getLocalizedSimulatorGamePath({
            gameId: id,
            versionChange,
            lang,
          })
        )
      }
    },
    [lang, router]
  )

  const openWorldGameProfileModal = useCallback(
    async ({
      gameId,
      projectId,
      fromGame = true,
    }: OpenWorldGameProfileModalOptions) => {
      const id = getSimulatorGameId({ gameId, projectId })
      if (!id) return

      try {
        const project = await getSimulatorGameDetail(id, lang)
        setModalState({ project, fromGame })
      } catch (error) {
        console.error('Failed to open simulator game profile modal:', error)
        router.push(withLocale(`/world/${id}/profile?fromGame=1`, lang))
      }
    },
    [lang, router]
  )

  const worldGameProfileModal = modalProject ? (
    <WorldGameProfileModal
      open
      project={modalProject}
      fromGame={modalState?.fromGame ?? false}
      onClose={closeWorldGameProfileModal}
      onStart={startModalGame}
    />
  ) : null

  return {
    navigateToSimulatorGame,
    openWorldGameProfileModal,
    worldGameProfileModal,
  }
}
